import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhoneCall } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  const [activeTab, setActiveTab] = useState('vision');

  return (
    <section id="about" className="section-padding bg-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Column - Image/Visual with 3D Float */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="relative"
            style={{ perspective: "1000px" }}
          >
            <motion.div 
              animate={{ 
                y: [-10, 10, -10],
                rotateX: [2, -2, 2],
                rotateY: [-2, 2, -2]
              }}
              transition={{ 
                duration: 6, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="relative rounded-2xl overflow-hidden shadow-2xl z-10"
            >
              <img 
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Appraiser examining documents" 
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
            
            {/* Decorative element */}
            <motion.div 
              animate={{ rotateZ: [0, 5, 0], scale: [1, 1.05, 1] }} 
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -right-6 w-48 h-48 bg-navy rounded-2xl -z-0"
            ></motion.div>
            
            <div className="absolute -top-6 -left-6 w-32 h-32 border-4 border-gold rounded-full -z-0 opacity-50"></div>
            
            {/* Callout Badge floating oppositely */}
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              style={{ transformStyle: "preserve-3d" }}
              className="absolute -bottom-8 left-8 bg-white p-6 rounded-xl shadow-soft z-20 max-w-[250px] border-l-4 border-gold"
            >
              <div className="flex items-center gap-4 mb-2">
                <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center text-navy">
                  <SafeIcon icon={FiPhoneCall} className="w-5 h-5" />
                </div>
                <p className="text-sm font-bold text-navy leading-tight">Call any time</p>
              </div>
              <a href="tel:+12148854504" className="text-lg font-serif font-bold text-navy hover:text-gold transition-colors inline-block pattern-link">
                +1-214-885-4504
              </a>
            </motion.div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-gold"></div>
              <span className="text-gold font-semibold uppercase tracking-wider text-sm">Who We Are</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Your Reliable Appraisal Partner, Supporting Every Step of Your Property Journey.
            </h2>
            
            <p className="text-muted text-lg mb-8">
              Certified residential and commercial appraisals across Texas — credible valuations to guide your real estate decisions.
            </p>

            {/* Tabs */}
            <div className="mb-8">
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  onClick={() => setActiveTab('vision')}
                  className={`pb-4 px-4 text-lg font-medium transition-colors relative ${activeTab === 'vision' ? 'text-navy' : 'text-muted hover:text-navy'}`}
                >
                  Our Vision
                  {activeTab === 'vision' && (
                    <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('mission')}
                  className={`pb-4 px-4 text-lg font-medium transition-colors relative ${activeTab === 'mission' ? 'text-navy' : 'text-muted hover:text-navy'}`}
                >
                  Our Mission
                  {activeTab === 'mission' && (
                    <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
                  )}
                </button>
              </div>

              <div className="min-h-[100px]">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="text-charcoal leading-relaxed"
                  >
                    {activeTab === 'vision' 
                      ? "To give every client the clarity and confidence they need to move forward in real estate — whether buying, selling, or investing."
                      : "To deliver credible, timely residential and commercial appraisals, backed by certified expertise and market knowledge, so you can make informed property decisions with peace of mind."
                    }
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>

            <a href="#contact" className="btn-primary">
              Talk to an Expert
            </a>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;